import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification } from '../common';

const ModulesSettings = () => {
  const { showNotification } = useNotification();
  const [modules, setModules] = useState({
    videoCourses: false,
    prospector: false,
    outlookCalendar: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [outlookConnectionStatus, setOutlookConnectionStatus] = useState({
    connected: false,
    loading: false,
    connecting: false,
    expires_at: null,
    is_expired: false
  });

  // Load current module settings
  useEffect(() => {
    fetchModuleSettings();
  }, []);

  // Check Outlook connection status when module is enabled
  useEffect(() => {
    if (modules.outlookCalendar) {
      checkOutlookConnectionStatus();
    }
  }, [modules.outlookCalendar]);

  const fetchModuleSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/settings/modules', {
        headers: { 'x-auth-token': token }
      });

      if (response.data) {
        setModules({
          videoCourses: response.data.video_courses_enabled || false,
          prospector: response.data.prospector_enabled || false,
          outlookCalendar: response.data.outlook_calendar_enabled || false
        });
      }
    } catch (error) {
      console.error('Error fetching module settings:', error);
      showNotification('Failed to load module settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleToggle = async (moduleName, enabled) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const updateData = {};
      if (moduleName === 'videoCourses') {
        updateData.video_courses_enabled = enabled;
      } else if (moduleName === 'prospector') {
        updateData.prospector_enabled = enabled;
      } else if (moduleName === 'outlookCalendar') {
        updateData.outlook_calendar_enabled = enabled;
      }

      await axios.put('/api/settings/modules', updateData, {
        headers: { 'x-auth-token': token }
      });

      setModules(prev => ({
        ...prev,
        [moduleName]: enabled
      }));

      showNotification(`${getModuleLabel(moduleName)} ${enabled ? 'enabled' : 'disabled'} successfully`, 'success');
      
      // Refresh the page to update navigation
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error updating module settings:', error);
      showNotification('Failed to update module settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getModuleLabel = (moduleName) => {
    switch (moduleName) {
      case 'videoCourses':
        return 'Video Courses';
      case 'prospector':
        return 'Prospector';
      case 'outlookCalendar':
        return 'Outlook Integration';
      default:
        return moduleName;
    }
  };

  const getModuleDescription = (moduleName) => {
    switch (moduleName) {
      case 'videoCourses':
        return 'Enable video course management and student access to educational content.';
      case 'prospector':
        return 'Enable company research and lead prospecting tools for finding new business opportunities. Currently supports Norwegian companies only.';
      case 'outlookCalendar':
        return 'Enable Microsoft Outlook integration for calendar events and email. View Outlook events in your CRM calendar and send/read emails through Outlook.';
      default:
        return '';
    }
  };

  const checkOutlookConnectionStatus = async () => {
    try {
      setOutlookConnectionStatus(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/outlook-calendar/status', {
        headers: { 'x-auth-token': token }
      });
      
      setOutlookConnectionStatus({
        connected: response.data.connected,
        loading: false,
        connecting: false,
        expires_at: response.data.expires_at,
        is_expired: response.data.is_expired
      });
    } catch (error) {
      console.error('Error checking Outlook connection status:', error);
      setOutlookConnectionStatus({
        connected: false,
        loading: false,
        connecting: false,
        expires_at: null,
        is_expired: false
      });
    }
  };

  const handleOutlookConnect = async () => {
    try {
      setOutlookConnectionStatus(prev => ({ ...prev, connecting: true }));
      const token = localStorage.getItem('token');
      
      // Get authorization URL
      const response = await axios.get('/api/outlook-calendar/auth-url', {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data.auth_url) {
        // Open OAuth popup window
        const popup = window.open(
          response.data.auth_url,
          'outlook-auth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );
        
        // Listen for popup messages
        const messageHandler = async (event) => {
          console.log('📨 Message received from popup:', event.data);
          console.log('Event origin:', event.origin);
          console.log('Window origin:', window.location.origin);
          
          if (event.origin !== window.location.origin) {
            console.log('❌ Origin mismatch, ignoring message');
            return;
          }
          
          if (event.data.type === 'OUTLOOK_AUTH_SUCCESS') {
            console.log('✅ Auth success message received');
            popup.close();
            window.removeEventListener('message', messageHandler);
            
            // Handle successful authorization
            try {
              console.log('🔄 Making OAuth callback request to backend...');
              const response = await axios.post('/api/outlook-calendar/oauth-callback', {
                code: event.data.code,
                state: event.data.state
              });
              console.log('✅ Backend callback successful:', response.data);
              
              showNotification('Outlook connected successfully!', 'success');
              checkOutlookConnectionStatus();
            } catch (error) {
              console.error('❌ OAuth callback error:', error);
              console.error('Error details:', error.response?.data);
              showNotification('Failed to complete Outlook connection', 'error');
            }
          } else if (event.data.type === 'OUTLOOK_AUTH_ERROR') {
            popup.close();
            window.removeEventListener('message', messageHandler);
            showNotification('Outlook authorization failed', 'error');
          }
        };
        
        window.addEventListener('message', messageHandler);
        
        // Check if popup was closed without completing auth
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);
            setOutlookConnectionStatus(prev => ({ ...prev, connecting: false }));
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error initiating Outlook connection:', error);
      if (error.response?.data?.message?.includes('not configured')) {
        showNotification('Microsoft OAuth is not configured on this server. Please contact your administrator to set up the integration.', 'error');
      } else {
        showNotification('Failed to start Outlook connection', 'error');
      }
      setOutlookConnectionStatus(prev => ({ ...prev, connecting: false }));
    }
  };

  const handleOutlookDisconnect = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/outlook-calendar/disconnect', {
        headers: { 'x-auth-token': token }
      });
      
      showNotification('Outlook Calendar disconnected successfully', 'success');
      setOutlookConnectionStatus({
        connected: false,
        loading: false,
        connecting: false,
        expires_at: null,
        is_expired: false
      });
    } catch (error) {
      console.error('Error disconnecting Outlook:', error);
      showNotification('Failed to disconnect Outlook Calendar', 'error');
    }
  };

  const getModuleIcon = (moduleName) => {
    switch (moduleName) {
      case 'videoCourses':
        return (
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'prospector':
        return (
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        );
      case 'outlookCalendar':
        return (
          <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Module Management</h3>
          <p className="mt-1 text-sm text-gray-500">
            Enable or disable modules for your company. Changes will take effect immediately and affect all users.
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {Object.entries(modules).map(([moduleName, isEnabled]) => (
              <div key={moduleName} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex-shrink-0">
                  {getModuleIcon(moduleName)}
                </div>
                
                <div className="flex-grow">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">
                        {getModuleLabel(moduleName)}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {getModuleDescription(moduleName)}
                      </p>
                      {moduleName === 'prospector' && (
                        <div className="mt-2 flex items-center text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Norwegian companies only
                        </div>
                      )}
                      
                      {/* Outlook Calendar Connection Status */}
                      {moduleName === 'outlookCalendar' && isEnabled && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              {outlookConnectionStatus.loading ? (
                                <div className="flex items-center text-sm text-gray-600">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                  Checking connection...
                                </div>
                              ) : outlookConnectionStatus.connected && !outlookConnectionStatus.is_expired ? (
                                <div className="flex items-center text-sm text-green-600">
                                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Connected to Outlook
                                </div>
                              ) : outlookConnectionStatus.is_expired ? (
                                <div className="flex items-center text-sm text-yellow-600">
                                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  Connection expired
                                </div>
                              ) : (
                                <div className="flex items-center text-sm text-gray-500">
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Not connected
                                </div>
                              )}
                            </div>
                            
                            <div className="flex space-x-2">
                              {outlookConnectionStatus.connected && !outlookConnectionStatus.is_expired ? (
                                <button
                                  onClick={handleOutlookDisconnect}
                                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                                >
                                  Disconnect
                                </button>
                              ) : (
                                <button
                                  onClick={handleOutlookConnect}
                                  disabled={outlookConnectionStatus.connecting}
                                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {outlookConnectionStatus.connecting ? 'Connecting...' : 'Connect'}
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {outlookConnectionStatus.connected && outlookConnectionStatus.expires_at && (
                            <div className="mt-2 text-xs text-gray-500">
                              Expires: {new Date(outlookConnectionStatus.expires_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center">
                      <button
                        type="button"
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                        role="switch"
                        aria-checked={isEnabled}
                        onClick={() => handleModuleToggle(moduleName, !isEnabled)}
                        disabled={saving}
                      >
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="ml-3 text-sm">
                        <span className={`font-medium ${isEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {saving && (
            <div className="mt-6 flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-sm text-gray-600">Updating modules...</span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Important Notice</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Disabling a module will immediately hide it from the navigation for all users in your company. 
                Any data associated with disabled modules will be preserved and restored when the module is re-enabled.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulesSettings;